import { createTourTranslationsConfig, type TourTranslation } from '@/config/objects/tour-translations';
import {
  CrudDetailView,
  PageContainer,
  useApiClient,
  type CrudDetailAction,
} from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  GenerateTranslationVoiceoverAction,
  GenerateTranslationVoiceoverIcon,
} from './actions';

/**
 * Tour translation detail page
 */
export function TourTranslationDetailPage() {
  const { id: tourId, translationId } = useParams<{ id: string; translationId: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [translation, setTranslation] = useState<TourTranslation | null>(null);

  // Modal states
  const [generateVoiceoverModalOpen, setGenerateVoiceoverModalOpen] = useState(false);

  /**
   * Delete translation mutation
   */
  const deleteMutation = useMutation({
    mutationFn: async (translationId: string): Promise<{ success: boolean; error?: string }> => {
      return apiClient.delete(`/tours/${tourId}/translations/${translationId}`);
    },
    onSuccess: (response: { success: boolean; error?: string }) => {
      if (response.success) {
        // Invalidate translations list to refresh data
        queryClient.invalidateQueries({ queryKey: ['Translation'] });
        navigate(`/tours/${tourId}`);
      } else {
        throw new Error(response.error || 'Failed to delete translation');
      }
    },
  });

  const translationsConfig = useMemo(
    () => createTourTranslationsConfig(tourId || ''),
    [tourId],
  );

  // Define custom detail actions
  const customActions: CrudDetailAction[] = useMemo(
    () => [
      {
        key: 'generate-voiceover',
        label: 'Generate Voiceover',
        icon: GenerateTranslationVoiceoverIcon,
        variant: 'secondary',
        onClick: () => setGenerateVoiceoverModalOpen(true),
      },
    ],
    [],
  );

  /**
   * Navigate to edit form for this translation
   */
  const handleEdit = (translation: TourTranslation) => {
    navigate(`/tours/${tourId}/translations/${translation.id}/edit`);
  };

  /**
   * Handle translation deletion
   */
  const handleDelete = async (translation: TourTranslation) => {
    return toast.promise(deleteMutation.mutateAsync(translation.id), {
      loading: 'Deleting translation...',
      success: 'Translation deleted successfully',
      error: (error: Error) => error?.message || 'Failed to delete translation',
    });
  };

  if (!tourId || !translationId) {
    return (
      <PageContainer>
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Translation Not Found</h2>
          <p className="text-muted-foreground">The requested translation could not be found.</p>
        </div>
      </PageContainer>
    );
  }

  const breadcrumbs = [
    { label: 'Home', href: '/' },
    { label: 'Tours', href: '/tours' },
    { label: translation?.tour?.name || 'Tour', href: `/tours/${tourId}` },
    { label: 'Translations', href: `/tours/${tourId}#translations` },
    { label: translation?.language?.name || 'Translation', current: true },
  ];

  return (
    <PageContainer>
      <div className="space-y-6">
        <CrudDetailView
          config={translationsConfig}
          itemId={translationId}
          breadcrumbs={breadcrumbs}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onData={(translationData: TourTranslation) => setTranslation(translationData)}
          customActions={customActions}
        />

        {/* Action Modals */}
        {tourId && translationId && (
          <GenerateTranslationVoiceoverAction
            tourId={tourId}
            translationId={translationId}
            open={generateVoiceoverModalOpen}
            onOpenChange={setGenerateVoiceoverModalOpen}
          />
        )}
      </div>
    </PageContainer>
  );
}
